// MemberProfile — shared profile component used by /my and /profile pages.
// Reads from findMe(s) which resolves to the viewed person (via ?u= query
// or current user). Sections: Hero, Details, Equity, Documents, Checklist.
//
// Config via parent element props:
//   editable  — whether fields are editable (boolean fn or static)
//   showPrivate — whether equity/docs/checklist are visible

import { findMe, canAdmin } from '../functions/index.js'
import { getSDK } from '../../shared/functions/sdk.js'
import * as grantsWriter from '../functions/grantsWriter.js'
import * as teams from '../functions/teams.js'
import { USE_PROFILES_OVERLAY } from '../functions/featureFlags.js'

// Map user_profiles column name → profiles column name. Columns absent
// from the map are user_profiles-only (financial) and are not mirrored.
// `department` and `group_name` used to live here; removed with §I10 —
// team membership is now Mongo-backed via `functions/teams.js`.
const _HR_TO_PROFILES = {
  name: 'display_name',
  position: 'title',
  github: 'github_username',
  location: 'location',
  timezone: 'timezone',
  weekly_hours: 'weekly_target_hours',
  working_hours_start: 'working_hours_start',
  working_hours_end: 'working_hours_end'
}

// Translate a user_profiles patch to a profiles patch. Returns null if
// no HR fields touched (e.g., pure financial edits) — skip the mirror.
const _toProfilesPatch = (patch) => {
  const out = {}
  for (const [k, v] of Object.entries(patch)) {
    const mapped = _HR_TO_PROFILES[k]
    if (!mapped) continue
    // profiles.weekly_target_hours is numeric; coerce.
    if (mapped === 'weekly_target_hours') {
      const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''))
      out[mapped] = Number.isFinite(n) ? n : null
    } else {
      out[mapped] = v
    }
  }
  return Object.keys(out).length ? out : null
}
import {
  liveShares as _liveShares,
  liveEquityPct as _liveEquityPct,
  SHARE_POOL,
  SHARE_CLASS_ADVISOR,
  SHARE_CLASS_OPTIONS,
  valuationOptions as _valuationOptions
} from '../functions/grantMath.js'

const _me = findMe
const _numberFmt = new Intl.NumberFormat('en-US')
const _fmt = (n) => (n == null ? '—' : _numberFmt.format(n))
const _timezoneOptions = () => {
  try {
    const list = typeof Intl !== 'undefined' && Intl.supportedValuesOf
      ? Intl.supportedValuesOf('timeZone')
      : null
    if (list && list.length) return list
  } catch {}
  return [
    'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'Asia/Tbilisi', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo',
    'Australia/Sydney', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Sao_Paulo'
  ]
}

const _countryOptions = [
  'Georgia', 'United States', 'United Kingdom', 'Germany', 'France', 'Spain',
  'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Poland', 'Ukraine', 'Turkey',
  'UAE', 'India', 'China', 'Japan', 'Australia', 'Brazil', 'Canada', 'Mexico',
  'Argentina', 'Ireland', 'Portugal', 'Italy', 'Greece', 'Czech Republic',
  'Hungary', 'Romania', 'Bulgaria', 'Estonia', 'Latvia', 'Lithuania'
]

// Legacy `location` field is a "City, CC" string. Extract country for the
// Country select; everything before the last comma becomes City.
const _countryCodes = {
  GE: 'Georgia', US: 'United States', UK: 'United Kingdom', GB: 'United Kingdom',
  DE: 'Germany', FR: 'France', ES: 'Spain', NL: 'Netherlands', SE: 'Sweden',
  NO: 'Norway', DK: 'Denmark', PL: 'Poland', UA: 'Ukraine', TR: 'Turkey',
  AE: 'UAE', IN: 'India', CN: 'China', JP: 'Japan', AU: 'Australia',
  BR: 'Brazil', CA: 'Canada', MX: 'Mexico', AR: 'Argentina', IE: 'Ireland',
  PT: 'Portugal', IT: 'Italy', GR: 'Greece', CZ: 'Czech Republic',
  HU: 'Hungary', RO: 'Romania', BG: 'Bulgaria', EE: 'Estonia', LV: 'Latvia',
  LT: 'Lithuania'
}

const _countryOf = (person) => {
  if (person?.country) return person.country
  const loc = person?.location || ''
  const parts = loc.split(',').map(s => s.trim()).filter(Boolean)
  const tail = parts[parts.length - 1] || ''
  if (_countryCodes[tail.toUpperCase()]) return _countryCodes[tail.toUpperCase()]
  if (_countryOptions.includes(tail)) return tail
  return ''
}

const _cityOf = (person) => {
  if (person?.city) return person.city
  const loc = person?.location || ''
  const parts = loc.split(',').map(s => s.trim()).filter(Boolean)
  if (parts.length >= 2) return parts.slice(0, -1).join(', ')
  return ''
}

const _tenure = (start) => {
  if (!start) return '—'
  const ms = Date.now() - new Date(start).getTime()
  const totalMonths = Math.floor(ms / (1000 * 60 * 60 * 24 * 30.44))
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`
  return `${years} year${years !== 1 ? 's' : ''} and ${months} month${months !== 1 ? 's' : ''}`
}

// Debounced Supabase write — per-field timers prevent cross-field cancellation
const _patchTimers = {}
const _patchUser = (s, patch) => {
  const person = _me(s)
  if (!person?.userId) return
  const next = (s.root.people || []).map((p) =>
    p.userId === person.userId ? { ...p, ...patch } : p
  )
  s.root.update({ people: next })
  const field = Object.keys(patch)[0]
  clearTimeout(_patchTimers[field])
  _patchTimers[field] = setTimeout(async () => {
    try {
      // Legacy write — user_profiles stays source of truth until the
      // profiles-overlay cutover completes.
      await getSDK().execute('workspaceProject.userProfiles', 'update', {
        id: person.userId,
        payload: patch
      })

      // Dual-write to public.profiles when the overlay flag is on.
      // profiles is email-keyed; upsert so a first-time edit creates
      // the row if the backfill hadn't run (defensive).
      // TODO[sdk-profiles-mirror]: no SDK route for the public.profiles
      // overlay yet — leave a Supabase fallback once the route lands.
      if (USE_PROFILES_OVERLAY && person.email) {
        const mapped = _toProfilesPatch(patch)
        if (mapped) {
          try {
            await getSDK().execute('workspaceProject.userProfiles', 'update', {
              id: person.userId,
              payload: { email: person.email, ...mapped, updated_at: new Date().toISOString() }
            })
          } catch (error) {
            console.warn('[MemberProfile] profiles mirror failed', error?.message)
          }
        }
      }
    } catch (e) {
      console.warn('patch failed', e)
    }
  }, 400)
}

// ── Grant CRUD helpers ─────────────────────────────────────────────
const _grantSaveTimers = {}
const _flushGrant = async (root, grantId) => {
  const patch = _pendingPatches[grantId]
  if (!patch || !Object.keys(patch).length) return
  delete _pendingPatches[grantId]
  root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'saving' } })
  try {
    const { data } = await grantsWriter.updateGrant(grantId, patch)
    if (data) {
      const merged = (root.userGrants || []).map(g => g.id === grantId ? data : g)
      root.update({ userGrants: merged })
    }
    root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'saved' } })
    clearTimeout(_grantSaveTimers[grantId])
    _grantSaveTimers[grantId] = setTimeout(() => {
      root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: null } })
    }, 2000)
  } catch (e) {
    console.warn('[grant flush]', e)
    root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'error' } })
  }
}

// Stage a change against root.userGrants (so inputs stay controlled) and
// record the pending patch against this grant id. NO auto-save here — the
// user must click the grant's Save button to flush. Status flips to
// 'dirty' so the Save button can highlight.
const _pendingPatches = {}
const _patchGrant = (root, grantId, patch) => {
  const current = (root.userGrants || []).find(g => g.id === grantId)
  if (!current) return
  const changed = Object.keys(patch).some(k => current[k] !== patch[k])
  if (!changed) return
  const next = (root.userGrants || []).map(g => g.id === grantId ? { ...g, ...patch } : g)
  _pendingPatches[grantId] = { ...(_pendingPatches[grantId] || {}), ...patch }
  root.update({
    userGrants: next,
    _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'dirty' }
  })
}

const _addGrant = async (root, userId) => {
  const currentValuation = root.companySettings?.current_valuation || 12000000
  // Sensible defaults so a freshly added grant already has every factor
  // populated and the computed shares/equity render immediately.
  const today = new Date()
  const oneYearOut = new Date(today); oneYearOut.setFullYear(oneYearOut.getFullYear() + 1)
  const iso = (d) => d.toISOString().slice(0, 10)
  try {
    const { data } = await grantsWriter.insertGrant({
      user_id: userId,
      share_class: 'B',
      vesting_months: 12,
      vesting_start: iso(today),
      vesting_end: iso(oneYearOut),
      origin_valuation: currentValuation,
      status: 'active',
      weekly_hours: '40',
      label: `Grant ${((root.userGrants || []).filter(g => g.user_id === userId).length + 1)}`
    })
    if (data) root.update({ userGrants: [...(root.userGrants || []), data] })
  } catch (e) { console.warn('[grant add]', e) }
}

// Investment record — investor stake calculated as (amount / valuation)
// × 10M pool. Stored as a grant row with `investment` set (the only
// field that flags this as an investment) and a real stock class the
// user can re-pick later (defaults to 'Preferred').
const _addInvestment = async (root, userId) => {
  const currentValuation = root.companySettings?.current_valuation || 12000000
  const raw = window.prompt('Investment amount ($):', '50000')
  if (raw == null) return
  const amount = Number(String(raw).replace(/[^0-9.]/g, ''))
  if (!amount || amount <= 0) return
  const shares = Math.round(amount / currentValuation * 10000000)
  try {
    const { data } = await grantsWriter.insertGrant({
      user_id: userId,
      share_class: 'Preferred',
      investment: amount,
      origin_valuation: currentValuation,
      shares_manual: shares,
      status: 'active',
      label: `Investment ${((root.userGrants || []).filter(g => g.user_id === userId && g.investment != null).length + 1)}`
    })
    if (data) root.update({ userGrants: [...(root.userGrants || []), data] })
  } catch (e) { console.warn('[investment add]', e) }
}

const _deleteGrant = async (root, grantId) => {
  try {
    await grantsWriter.deleteGrant(grantId)
    root.update({ userGrants: (root.userGrants || []).filter(g => g.id !== grantId) })
    delete _pendingPatches[grantId]
    clearTimeout(_grantSaveTimers[grantId])
    delete _grantSaveTimers[grantId]
  } catch (e) { console.warn('[grant delete]', e) }
}

// Sum of shares + vested across all of this user's grants + investments.
// Total `shares` includes everything the user owns (grants + investments).
// `vested` counts grants by their vested_shares; investments are fully
// owned at purchase, so their full share count counts toward vested.
const _userShareTotals = (s) => {
  const me = _me(s)
  const grants = (s.root?.userGrants || []).filter(g => g.user_id === me.userId)
  return grants.reduce((a, g) => {
    const shares = Number(g.shares) || 0
    const vested = g.investment != null ? shares : (Number(g.vested_shares) || 0)
    return { shares: a.shares + shares, vested: a.vested + vested }
  }, { shares: 0, vested: 0 })
}

// 3-state status derivation: end date in the past → 'completed', status
// 'left' → 'ended', otherwise 'active'. Matches the cap-table pill logic.
const _grantEffectiveStatus = (g) => {
  const end = g.vesting_end ? new Date(g.vesting_end) : null
  if (end && end < new Date()) return 'completed'
  if (g.status === 'left') return 'ended'
  return g.status || 'active'
}

// Contract value = salary_monthly × months(start, end)
const _contractValue = (g) => {
  if (!g?.salary_monthly || !g?.vesting_start || !g?.vesting_end) return 0
  const months = (new Date(g.vesting_end) - new Date(g.vesting_start)) / (1000 * 60 * 60 * 24 * 30.4375)
  return Math.round((Number(g.salary_monthly) || 0) * months)
}

// Derive hourly rate from salary_monthly / (weekly_hours × 4.33). Returns
// null when either is missing. Used to backfill the Hourly input when the
// DB has monthly + hours but no explicit hourly.
const _deriveHourly = (g) => {
  const mo = Number(g?.salary_monthly) || 0
  const wh = Number(String(g?.weekly_hours || '').replace(/[^0-9.]/g, '')) || 0
  if (!mo || !wh) return null
  return Math.round((mo / (wh * 4.33)) * 100) / 100
}

// Derive monthly from hourly × weekly_hours × 4.33. Returns null when
// either is missing. Used to backfill the Monthly input when the DB has
// hourly + hours but no explicit monthly.
const _deriveMonthly = (g) => {
  const hr = Number(g?.hourly_rate) || 0
  const wh = Number(String(g?.weekly_hours || '').replace(/[^0-9.]/g, '')) || 0
  if (!hr || !wh) return null
  return Math.round(hr * wh * 4.33)
}

// DOMQL's reactive update cycle briefly detaches and re-attaches input
// elements, which blurs them. Inputs that dispatch _patchGrant on every
// keystroke therefore lose focus after each character. This helper
// re-focuses the same node (and restores caret where possible) after
// the update settles, so typing feels uninterrupted.
//
// We retry across several microtasks + rAF frames because DOMQL may
// run multiple render passes for a single state update, each one
// potentially detaching the element — a single setTimeout(0) only
// catches the first blur.
const _keepFocus = (target) => {
  if (!target) return
  const supportsSel = target.type === 'text' || target.type === 'search' || target.tagName === 'TEXTAREA'
  const selStart = supportsSel ? target.selectionStart : null
  let attempts = 0
  const refocus = () => {
    if (!document.body.contains(target)) return
    if (document.activeElement !== target) {
      target.focus()
      if (selStart != null) {
        try { target.setSelectionRange(selStart, selStart) } catch {}
      }
    }
    if (attempts++ < 6) setTimeout(refocus, 20)
  }
  // Fire on microtask + next frame so we catch the earliest possible
  // re-attach point.
  Promise.resolve().then(refocus)
  requestAnimationFrame(refocus)
}

// Row builder — label/value with optional inline editing
const _row = (label, valueFn, opts = {}) => ({
  flow: 'x',
  align: 'center space-between',
  gap: 'B',
  padding: 'B 0',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  hide: opts.hide || null,
  RowLabel: opts.hint
    ? { flow: 'y', gap: 'Y', LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: label }, Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: opts.hint } }
    : { tag: 'span', fontSize: 'A', color: 'caption', text: label },
  // Editable rows render two views side-by-side: a static span shown when
  // not in edit mode, and an input/select shown when editing.
  RowValue: opts.editable
    ? {
        flow: 'x',
        align: 'center right',
        ReadView: {
          hide: (el, s) => !!s.root?._profileEditing && el.call('canEditProfile', _me(s)),
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: opts.accent || 'title',
          text: valueFn
        },
        EditView: opts.options
          ? {
              hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', _me(s)),
              tag: 'select',
              fontSize: 'A',
              fontWeight: '700',
              color: opts.accent || 'title',
              background: 'overlayStrong',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'line',
              round: 'Y',
              padding: 'V Z',
              cursor: 'pointer',
              width: 'F',
              ':focus': { borderColor: 'blue', outline: 'none' },
              value: (el, s) => {
                const v = valueFn(el, s)
                return v === '—' ? '' : (v || '')
              },
              onCreate: (el, s) => {
                const v = valueFn(el, s)
                if (el.node) el.node.value = v === '—' ? '' : (v || '')
              },
              childExtends: { tag: 'option', text: (el, s) => s.text || s.value || '' },
              childrenAs: 'state',
              children: (el, s) => {
                const list = typeof opts.options === 'function' ? opts.options() : opts.options
                return [{ value: '', text: '— none —' }, ...list.map(v => ({ value: v, text: v }))]
              },
              onChange: (e, el, s) => {
                if (opts.field) _patchUser(s, { [opts.field]: e.target.value || null })
              }
            }
          : {
              hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', _me(s)),
              tag: 'input',
              type: opts.type || 'text',
              fontSize: 'A',
              fontWeight: '700',
              color: opts.accent || 'title',
              background: 'overlayStrong',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'line',
              round: 'Y',
              textAlign: 'right',
              padding: 'V Z',
              width: 'F',
              placeholder: opts.placeholder || '',
              ':focus': { borderColor: 'blue', outline: 'none' },
              value: opts.editValueFn || valueFn,
              onInput: (e, el, s) => {
                if (!opts.field) return
                const parsed = opts.parse ? opts.parse(e.target.value) : e.target.value
                _patchUser(s, { [opts.field]: parsed })
              }
            }
      }
    : {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: opts.accent || 'title',
        text: valueFn
      }
})

// Card wrapper shorthand
const _card = (content) => ({
  flow: 'y',
  padding: 'C',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  ...content
})

// ── Exported component ──

export const MemberProfile = {
  flow: 'y',
  gap: 'D',
  ProfileHero: _card({
    attr: { 'data-content-section': 'Profile' },
    flow: 'x',
    align: 'center',
    gap: 'C',
    padding: 'C',

    HeroAvatar: {
      align: 'center center',
      boxSize: 'D2',
      round: '50%',
      color: 'white',
      fontSize: 'C',
      fontWeight: '800',
      background: 'blue',
      text: (el, s) => _me(s).avatar || ''
    },
    HeroInfo: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      HeroName: {
        tag: 'h2',
        fontSize: 'D',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.02em',
        text: (el, s) => _me(s).name || 'Unknown'
      },
      HeroPosition: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        text: (el, s) => _me(s).position || ''
      },
      HeroEmail: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => _me(s).email || s.root.currentUserEmail || ''
      }
    },

    HeroActions: {
      hide: (el, s) => !el.call('canEditProfile', _me(s)),
      flow: 'x',
      gap: 'Z',
      align: 'center right',

      EditBtn: {
        hide: (el, s) => !!s.root?._profileEditing,
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        background: 'overlayStrong',
        color: 'title',
        ':hover': { background: 'blue', color: 'white' },
        text: 'Edit',
        onClick: (e, el, s) => s.root.update({ _profileEditing: true })
      },

      CancelBtn: {
        hide: (el, s) => !s.root?._profileEditing,
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        background: 'transparent',
        color: 'caption',
        ':hover': { color: 'title' },
        text: 'Done',
        onClick: (e, el, s) => s.root.update({ _profileEditing: false })
      },

      SaveBtn: {
        hide: (el, s) => !s.root?._profileEditing,
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '700',
        background: 'blue',
        color: 'white',
        ':hover': { opacity: '.9' },
        text: 'Save',
        onClick: (e, el, s) => s.root.update({ _profileEditing: false })
      }
    }
  }),

  ProfileDetails: _card({
    FullName: _row('Full name', (el, s) => _me(s).name || '—', {
      editable: true,
      field: 'name',
      placeholder: 'Your name'
    }),
    Position: _row('Position', (el, s) => _me(s).position || '—', {
      editable: true,
      field: 'position',
      placeholder: 'e.g. Frontend Engineer'
    }),
    // Team picker — reads from `root.teamsByEmail` (Mongo-sourced via
    // `functions/teams.js`). Writes go through SDK (add/remove Team
    // members). No user_profiles text fallback — legacy columns dropped.
    Team: {
      flow: 'x',
      align: 'center space-between',
      gap: 'B',
      padding: 'B 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: { tag: 'span', fontSize: 'A', color: 'caption', text: 'Team' },
      RowValue: {
        flow: 'x',
        align: 'center right',
        gap: 'Z',
        ReadView: {
          hide: (el, s) => !!s.root?._profileEditing && el.call('canEditProfile', _me(s)),
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => teams.primaryTeamName(_me(s)?.email, s.root) || '—'
        },
        EditSelect: {
          hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', _me(s)),
          tag: 'select',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          background: 'overlayStrong',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          round: 'Y',
          padding: 'V Z',
          cursor: 'pointer',
          minWidth: 'F',
          ':focus': { borderColor: 'blue', outline: 'none' },
          onCreate: (el, s) => {
            const current = teams.primaryTeamName(_me(s)?.email, s.root) || ''
            if (el.node) el.node.value = current
          },
          childExtends: { tag: 'option', value: (el, s) => s.value, text: (el, s) => s.text },
          childrenAs: 'state',
          children: (el, s) => {
            const orgId = (s.root?.jwtClaims?.workspace_org_id)
              || (s.root?.jwtClaims?.orgs || [])[0]?.id
            const list = teams.teamsInOrg(orgId, s.root)
            return [
              { value: '', text: '— none —' },
              ...list.map((t) => ({ value: t.name, text: t.name })),
              { value: '__create__', text: '+ Create team…' }
            ]
          },
          onChange: async (e, el, s) => {
            const me = _me(s)
            if (!me?.userId || !me?.email) return
            const root = s.root
            const orgId = (root?.jwtClaims?.workspace_org_id)
              || (root?.jwtClaims?.orgs || [])[0]?.id
            if (!orgId) return
            const value = e.target.value
            const current = (root.teamsByEmail?.[me.email.toLowerCase()] || [])[0]
            try {
              if (value === '__create__') {
                const name = typeof window !== 'undefined'
                  ? window.prompt('New team name')
                  : null
                if (!name) {
                  if (el.node) el.node.value = current?.name || ''
                  return
                }
                const team = await teams.createTeam(root, { orgId, name: name.trim() })
                if (current && current.orgId === orgId) {
                  await teams.removeMemberFromTeam(root, { orgId, teamId: current.id, userId: me.userId, email: me.email })
                }
                await teams.addMemberToTeam(root, { orgId, teamId: team.id, userId: me.userId, email: me.email })
                if (el.node) el.node.value = team.name
                return
              }
              if (current && current.orgId === orgId) {
                await teams.removeMemberFromTeam(root, { orgId, teamId: current.id, userId: me.userId, email: me.email })
              }
              if (value) {
                const target = teams.teamsInOrg(orgId, root).find((t) => t.name === value)
                if (target) {
                  await teams.addMemberToTeam(root, { orgId, teamId: target.id, userId: me.userId, email: me.email })
                }
              }
            } catch (err) {
              console.warn('[MemberProfile] team update failed', err)
              if (el.node) el.node.value = current?.name || ''
            }
          }
        }
      }
    },
    Country: _row('Country', (el, s) => _countryOf(_me(s)) || '—', {
      editable: true,
      field: 'country',
      options: _countryOptions
    }),
    City: _row('City', (el, s) => _cityOf(_me(s)) || '—', {
      editable: true,
      field: 'city',
      placeholder: 'City'
    }),
    Timezone: _row('Timezone', (el, s) => _me(s).timezone || '—', {
      editable: true,
      field: 'timezone',
      options: _timezoneOptions
    }),
    GitHub: _row('GitHub', (el, s) => _me(s).github || '—', {
      editable: true,
      field: 'github',
      placeholder: 'username'
    }),
    Tenure: _row('Tenure', (el, s) => _tenure(_me(s).startDate)),
    Role: _row('Role', (el, s) => {
      // CU-10 §90: roles no longer on person. Look up from root.roles.byUser.
      const me = _me(s)
      const userId = me?.userId
      const byUser = s.root?.roles?.byUser || {}
      const role = (userId && byUser[userId]) || (me?.roles?.[0]) || 'member'
      return role
    }, { hide: (el) => !el.call('isSuperAdmin') })
  }),

  ProfileAvailability: {
    ..._card({}),
    attr: { 'data-content-section': 'Availability' },
    // Availability only applies to people currently on the team
    hide: (el, s) => {
      const me = _me(s)
      if (!me?.email) return true
      if (me.status === 'left') return true
      // CU-10 §90: role from root.roles.byUser or legacy me.roles fallback
      const coreRoles = new Set(['superadmin', 'admin', 'manager', 'member'])
      const byUser = s.root?.roles?.byUser || {}
      const legacyRoles = Array.isArray(me.roles) && me.roles.length > 0 ? me.roles : null
      if (legacyRoles) return !legacyRoles.some(r => coreRoles.has(r))
      const assignedRole = (me.userId && byUser[me.userId]) || 'member'
      return !coreRoles.has(assignedRole)
    },
    gap: 'Z',
    AvailHeader: {
      flow: 'x',
      align: 'center space-between',
      AvailTitle: { tag: 'span', text: 'Availability', fontSize: 'Z2', fontWeight: '600', color: 'subtitle' },
      AvailHint: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          if (!s.root?.currentUserEmail) return 'Read only'
          // CU-10 §90: use canAdmin() — unions JWT perm + legacy byUser role.
          const admin = canAdmin(s)
          return admin ? 'Click to toggle (admin)' : 'Read only'
        }
      }
    },
    ProfileAvailabilityGrid: {}
  },

  ProfileEquity: {
    ..._card({}),
    attr: { 'data-content-section': 'Equity grant' },
    hide: (el, s) => !el.call('canAccessPrivate', _me(s)),
    gap: 'C',
    position: 'relative',
    overflow: 'hidden',

    EqGlow: {
      position: 'absolute',
      top: '-50%',
      right: '-10%',
      width: '420px',
      height: '420px',
      round: '50%',
      background: 'green',
      opacity: '0.12',
      filter: 'blur(80px)'
    },

    EqHead: {
      flow: 'y',
      gap: 'Z',
      EqEyebrow: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'green',
        text: 'Equity grant'
      },
      EqTitle: {
        tag: 'h2',
        fontSize: 'C',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.02em',
        text: 'Stocks'
      }
    },

    EqBig: {
      flow: 'x',
      align: 'baseline',
      gap: 'A',
      EqVal: {
        tag: 'span',
        fontSize: 'F',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.04em',
        lineHeight: '1',
        text: (el, s) => _fmt(_userShareTotals(s).shares)
      },
      EqUnit: {
        tag: 'span',
        fontSize: 'A',
        color: 'caption',
        fontWeight: '600',
        text: 'shares granted'
      }
    },

    VestingBar: {
      flow: 'y',
      gap: 'Y',
      padding: 'Z 0',
      BarLabel: {
        flow: 'x',
        align: 'center space-between',
        VestLabel: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => {
            const t = _userShareTotals(s)
            if (!t.shares) return 'No vesting data'
            const pct = Math.min(100, Math.round((t.vested / t.shares) * 100))
            return `${pct}% vested`
          }
        },
        VestRemain: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => {
            const t = _userShareTotals(s)
            const remaining = Math.max(0, t.shares - t.vested)
            return remaining > 0 ? `${_fmt(remaining)} remaining` : 'Fully vested'
          }
        }
      },
      BarTrack: {
        height: 'Y',
        round: 'Y',
        background: 'line',
        overflow: 'hidden',
        BarFill: {
          height: '100%',
          round: 'Y',
          background: 'green',
          width: (el, s) => {
            const t = _userShareTotals(s)
            if (!t.shares) return '0%'
            return `${Math.min(100, Math.round((t.vested / t.shares) * 100))}%`
          }
        }
      }
    },

    // Column headers — one line identifying what each column of the rows below means.
    GrantsHeader: {
      display: 'grid',
      gridTemplateColumns: '1.4fr 0.8fr 1.6fr 1fr 0.8fr 1fr',
      gap: 'Z',
      padding: 'Z 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      H00_Label: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', text: 'Grant' },
      H01_Class: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', text: 'Class' },
      H02_Period: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', text: 'Period' },
      H03_Shares: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Shares' },
      H04_Equity: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Equity' },
      H05_Value: { fontSize: 'Y1', fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Value' }
    },

    // One row per grant (including share_class='investor' records).
    // Row renders shares, equity %, and — for investments — origin valuation
    // & amount. Uses `childrenAs: 'state'` so each row's state = the grant.
    GrantsRows: {
      flow: 'y',
      childrenAs: 'state',
      children: (el, s) => {
        const me = _me(s)
        return (s.root?.userGrants || [])
          .filter(g => g.user_id === me.userId)
          .slice()
          .sort((a, b) => (b.shares || 0) - (a.shares || 0))
      },
      childExtends: {
        display: 'grid',
        gridTemplateColumns: '1.4fr 0.8fr 1.6fr 1fr 0.8fr 1fr',
        gap: 'Z',
        padding: 'Z 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',

        C00_Label: {
          flow: 'y',
          gap: 'V',
          Name: {
            tag: 'span',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => s.label || (s.investment != null ? 'Investment' : 'Grant')
          },
          Sub: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            text: (el, s) => {
              if (s.investment != null) {
                return `$${_fmt(s.investment)} invested`
              }
              if (s.salary_monthly) return `$${_fmt(Math.round(s.salary_monthly))}/mo`
              return ''
            }
          }
        },

        C01_Class: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          color: (el, s) => {
            const cls = (s.share_class || '').toLowerCase()
            if (cls === 'a') return 'green'
            if (cls === 'b') return 'blue'
            if (cls === 'c') return 'orange'
            if (cls === 'preferred') return 'purple'
            if (cls === 'common') return 'subtitle'
            return 'subtitle'
          },
          textTransform: 'uppercase',
          text: (el, s) => s.share_class || '—'
        },

        C02_Period: {
          flow: 'y',
          gap: 'V',
          Range: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'title',
            text: (el, s) => {
              if (s.investment != null) {
                return s.origin_valuation ? `@ $${_fmt(s.origin_valuation)}` : '—'
              }
              const a = s.vesting_start || '—'
              const b = s.vesting_end || '—'
              return `${a} → ${b}`
            }
          },
          Status: {
            tag: 'span',
            fontSize: 'Y2',
            color: (el, s) => {
              if (s.investment != null) return 'caption'
              const st = _grantEffectiveStatus(s)
              if (st === 'active') return 'green'
              if (st === 'completed') return 'blue'
              return 'caption'
            },
            fontWeight: '600',
            // Investments are paid-up at purchase — no status timeline.
            text: (el, s) => s.investment != null ? '' : _grantEffectiveStatus(s)
          }
        },

        C03_Shares: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          textAlign: 'right',
          text: (el, s) => _fmt(s.shares)
        },

        C04_Equity: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'blue',
          textAlign: 'right',
          text: (el, s) => s.shares ? `${((s.shares / 10000000) * 100).toFixed(2)}%` : '—'
        },

        C05_Value: {
          flow: 'y',
          gap: 'V',
          ValMain: {
            tag: 'span',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            textAlign: 'right',
            text: (el, s) => {
              const v = s.root?.companySettings?.current_valuation
              if (!v || !s.shares) return '—'
              return `$${_fmt(Math.round((s.shares / 10000000) * v))}`
            }
          },
          ValVested: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            textAlign: 'right',
            // Investments are fully owned at purchase — no vesting figure.
            text: (el, s) => s.investment != null ? '' : `${_fmt(s.vested_shares || 0)} vested`
          }
        }
      }
    },

    // No grants placeholder — shown only when the user has no records yet.
    GrantsEmpty: {
      hide: (el, s) => {
        const me = _me(s)
        return (s.root?.userGrants || []).some(g => g.user_id === me.userId)
      },
      tag: 'span',
      padding: 'B 0',
      fontSize: 'Z2',
      color: 'caption',
      text: 'No grants or investments yet.'
    },

    // Aggregate totals — valuation + % of company across all of user's grants.
    EqTotals: {
      flow: 'y',
      padding: 'A 0 0 0',

      EqCurrentValuation: _row(
        'Current valuation',
        (el, s) => {
          const v = s.root?.companySettings?.current_valuation
          return v ? `$${_fmt(v)}` : '—'
        },
        { accent: 'blue' }
      ),
      EqEquityPct: _row(
        '% of company',
        (el, s) => {
          const shares = _userShareTotals(s).shares
          if (!shares) return '—'
          return `${((shares / 10000000) * 100).toFixed(4)}%`
        },
        { accent: 'blue' }
      ),
      EqInvestedTotal: _row(
        'Total invested',
        (el, s) => {
          const me = _me(s)
          const total = (s.root?.userGrants || [])
            .filter(g => g.user_id === me.userId)
            .reduce((a, g) => a + (Number(g.investment) || 0), 0)
          return total > 0 ? `$${_fmt(total)}` : '—'
        },
        {
          accent: 'purple',
          hide: (el, s) => {
            const me = _me(s)
            return !(s.root?.userGrants || []).some(g => g.user_id === me.userId && g.investment)
          }
        }
      ),
      EqMonthlyStock: _row(
        'Monthly stock earnings',
        (el, s) => {
          const me = _me(s)
          const grants = (s.root?.userGrants || []).filter(g => g.user_id === me.userId && g.share_class !== 'investor')
          const perMo = grants.reduce((a, g) => {
            if (!g.vesting_start || !g.vesting_end || !g.shares) return a
            const months = (new Date(g.vesting_end) - new Date(g.vesting_start)) / (1000 * 60 * 60 * 24 * 30.4375)
            return a + (months > 0 ? (g.shares / months) : 0)
          }, 0)
          return perMo > 0 ? `${_fmt(Math.round(perMo))}/mo` : '—'
        },
        {
          accent: 'green',
          hide: (el, s) => {
            const me = _me(s)
            return !(s.root?.userGrants || []).some(g => g.user_id === me.userId && g.share_class !== 'investor')
          }
        }
      ),
      EqMonthlySalary: _row(
        'Monthly salary',
        (el, s) => {
          const me = _me(s)
          const total = (s.root?.userGrants || [])
            .filter(g => g.user_id === me.userId)
            .reduce((a, g) => a + (Number(g.salary_monthly) || 0), 0)
          return total > 0 ? `$${_fmt(Math.round(total))}` : '—'
        },
        {
          hide: (el, s) => {
            const me = _me(s)
            return !(s.root?.userGrants || []).some(g => g.user_id === me.userId && g.salary_monthly)
          }
        }
      )
    }
  },

  // ── Grants editor — 4 key factors per grant, multi-grant support ──
  // Only visible in edit mode; profile view stays clean.
  ProfileGrants: {
    ..._card({}),
    attr: { 'data-content-section': 'Grants' },
    hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', _me(s)),
    gap: 'B',

    GrantsHead: {
      flow: 'x',
      align: 'baseline space-between',
      gap: 'A',
      Title: {
        tag: 'h3',
        margin: '0',
        fontSize: 'B',
        fontWeight: '800',
        color: 'title',
        text: 'Equity grants'
      },
      Subtitle: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: 'Start, End, Origin valuation, and Monthly salary — shares + equity % compute automatically.'
      }
    },

    GrantsList: {
      flow: 'y',
      gap: 'B',
      childExtends: 'ProfileGrantCard',
      childrenAs: 'state',
      // Stable `key` per grant so DOMQL reconciles children by id instead
      // of rebuilding the whole card tree on every keystroke. Because
      // childrenAs='state' wraps each array item into `{ state: item }`,
      // a `key` sitting on the item itself never bubbles up to the
      // top-level child definition — so we route it through childProps,
      // which is merged at the top level before the reconciler reads
      // `childDef.key`.
      childProps: (el, grant) => ({ key: grant?.id || 'none' }),
      children: (el, s) => {
        const me = _me(s)
        return (s.root?.userGrants || []).filter(g => g.user_id === me.userId)
      }
    },

    AddButtons: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',

      AddGrantBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        background: 'overlayStrong',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '600',
        cursor: 'pointer',
        text: '+ Add grant',
        ':hover': { background: 'blue', color: 'white' },
        onClick: (e, el, s) => _addGrant(s.root, _me(s).userId)
      },

      AddInvestmentBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        background: 'overlayStrong',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '600',
        cursor: 'pointer',
        text: '+ Add investment',
        ':hover': { background: 'purple', color: 'white' },
        onClick: (e, el, s) => _addInvestment(s.root, _me(s).userId)
      }
    }
  },

  ProfileDocuments: {
    ..._card({}),
    attr: { 'data-content-section': 'Documents' },
    hide: (el, s) => !el.call('canAccessPrivate', _me(s)),
    gap: 'C',

    DocHead: {
      flow: 'y',
      gap: 'Z',
      DocEyebrow: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'blue',
        text: 'Documents'
      },
      DocTitle: {
        tag: 'h2',
        fontSize: 'C',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.02em',
        text: 'Signed'
      }
    },

    onCreate: async (el, s) => {
      const person = _me(s)
      if (!person?.userId) return
      // Skip fetch when viewing self — app.js already loads into root.userDocs
      if (person.userId === s.root?.currentUserId) return
      try {
        const data = await el.sdk.execute('workspaceProject.documents.userDocuments', 'list', {
          userId: person.userId
        })
        if (data) el.state.update({ _docs: data })
      } catch (e) { /* RLS will block if not self/superadmin */ }
    },

    DocList: {
      flow: 'y',
      childExtends: {
        flow: 'x',
        align: 'center',
        gap: 'A',
        padding: 'Z1 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        cursor: (el, s) => s.storagePath ? 'pointer' : 'default',
        ':hover': { background: 'overlayStrong' },
        // migrated 2026-04-27 to SDK route workspaceProject.storage.signedUrl
        // (replaces sb().storage.from('contracts').createSignedUrl(path, 300)).
        onClick: async (e, el, s) => {
          if (!s.storagePath) return
          try {
            const result = await el.sdk.execute('workspaceProject.storage', 'signedUrl', {
              bucket: 'contracts',
              path: s.storagePath,
              ttl: 300,
            })
            const url = result?.url || result?.signedUrl
            if (url) window.open(url, '_blank')
            else console.warn('[MemberProfile] signed URL response missing url field', result)
          } catch (err) { console.warn('[MemberProfile] signed url failed', err?.message || err) }
        },
        DocIcon: { tag: 'span', fontSize: 'B', text: '\uD83D\uDCC4' },
        DocInfo: {
          flow: 'y',
          gap: 'V',
          flex: '1',
          DocTitle: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: (el, s) => s.title },
          DocMeta: { tag: 'span', fontSize: 'Z2', color: 'caption', text: (el, s) => [s.type, s.size, s.signedAt ? `signed ${s.signedAt}` : ''].filter(Boolean).join(' · ') }
        },
        DocAction: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'blue',
          textTransform: 'uppercase',
          text: (el, s) => s.storagePath ? 'Open' : ''
        }
      },
      childrenAs: 'state',
      children: (el, s) => {
        const docs = s._docs || s.root?.userDocs || []
        return docs.length
          ? docs.map((d) => ({
              id: d.id,
              title: d.title,
              type: 'PDF',
              size: d.file_size ? `${Math.round(d.file_size / 1024)} KB` : '—',
              signedAt: d.signed_at || '—',
              storagePath: d.storage_path
            }))
          : [
              {
                id: 'empty',
                title: 'No documents yet',
                type: '',
                size: '',
                signedAt: ''
              }
            ]
      }
    },

  },

  ProfileChecklist: {
    ..._card({}),
    attr: { 'data-content-section': 'Checklist' },
    hide: (el, s) => !el.call('canAccessPrivate', _me(s)),
    gap: 'B',

    CheckHead: {
      flow: 'y',
      gap: 'Z',
      CheckEyebrow: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'orange',
        text: 'Onboarding'
      },
      CheckTitle: {
        tag: 'h2',
        fontSize: 'C',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.02em',
        text: 'Profile Checklist'
      }
    },

    CheckList: {
      flow: 'y',
      gap: 'Y',
      childExtends: 'MemberProfileCheckItem',
      childrenAs: 'state',
      children: (el, s) => {
        const p = _me(s)
        const docs = s.root?.userDocs || []
        return [
          {
            label: 'Freelance Contractor Agreement',
            done: docs.some((d) => d.type === 'contract')
          },
          { label: 'Equity Grant Agreement', done: !!(p.shares || p.stocks) },
          {
            label: 'Non-Disclosure Agreement',
            done: docs.some((d) => d.type === 'nda')
          },
          {
            label: 'Code of Conduct',
            done: docs.some((d) => d.type === 'coc')
          },
          { label: 'IP Assignment', done: docs.some((d) => d.type === 'ip') },
          {
            label: 'Location & Timezone set',
            done: !!(p.location && p.timezone)
          },
          { label: 'Availability configured', done: !!p.workingDays },
          { label: 'GitHub connected', done: !!p.github }
        ]
      }
    }
  }
}

const _selfCard = (content) => ({
  ..._card(content),
  hide: (el) => !el.call('isSelf')
})

const _savePrefs = async (root, patch) => {
  if (!root?.currentUserId) return
  const prefs = { ...(root.userPrefs || {}), ...patch, user_id: root.currentUserId }
  root.update({ userPrefs: prefs })
  try {
    await getSDK().execute('workspaceProject.userPreferences', 'update', { payload: prefs })
  } catch (e) { console.warn('prefs save failed', e) }
}

// ── Security (self only) ──
MemberProfile.ProfileSecurity = _selfCard({
  attr: { 'data-content-section': 'Security' },
  gap: 'B',
  SecurityTitle: { tag: 'h2', fontSize: 'B', fontWeight: '700', color: 'title', text: 'Security' },
  SecurityRows: {
    flow: 'y',

    PwdRow: {
      flow: 'x',
      align: 'flex-start space-between',
      gap: 'B',
      padding: 'B 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Password' },
        Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'Change your login password.' }
      },
      PwdAction: {
        flow: 'x',
        gap: 'Z',
        align: 'center',
        PwdInput: {
          tag: 'input',
          type: 'password',
          placeholder: 'New password',
          fontSize: 'Z2',
          padding: 'Y Z1',
          round: 'Y',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'overlay',
          color: 'title',
          width: 'F'
        },
        PwdBtn: {
          tag: 'button',
          padding: 'Y Z1',
          round: 'Y',
          border: 'none',
          background: 'blue',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '600',
          cursor: 'pointer',
          text: (el, s) => s._pwdStatus || 'Update',
          onClick: async (e, el, s) => {
            const input = el.parent.PwdInput?.node
            const pw = input?.value
            if (!pw || pw.length < 6) return
            try {
              // TODO[password-update]: SDK updateMe currently routes profile
              // edits — confirm server-side handles password rotation via this
              // path; if a dedicated flow is required, replace with that call.
              await el.sdk.updateMe({ password: pw })
              input.value = ''
              el.state.update({ _pwdStatus: 'Updated!' })
              setTimeout(() => el.state.update({ _pwdStatus: null }), 2000)
            } catch (err) {
              el.state.update({ _pwdStatus: 'Failed' })
              setTimeout(() => el.state.update({ _pwdStatus: null }), 2000)
            }
          }
        }
      }
    },

    TwoFa: _row('Two-factor auth', 'Coming soon', { hint: 'TOTP authenticator support planned.' }),
    BackupCodes: _row('Backup codes', 'Coming soon', {})
  }
})

// ── Notifications (self only) ──
MemberProfile.ProfileNotifications = _selfCard({
  attr: { 'data-content-section': 'Notifications' },
  gap: 'B',
  NotifTitle: { tag: 'h2', fontSize: 'B', fontWeight: '700', color: 'title', text: 'Notifications' },
  NotifRows: {
    flow: 'y',

    EmailDigest: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Email digest' },
        Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'Summary of announcements and meetings.' }
      },
      DigestSelect: {
        tag: 'select',
        padding: 'Y Z1',
        round: 'Y',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        cursor: 'pointer',
        childExtends: { tag: 'option', text: (el, s) => s.text || s.value || '' },
        childrenAs: 'state',
        children: (el, s) => {
          const cur = s.root?.userPrefs?.email_digest || 'daily'
          return ['off', 'daily', 'weekly'].map(v => ({
            value: v,
            text: v.charAt(0).toUpperCase() + v.slice(1),
            selected: v === cur
          }))
        },
        onChange: (e, el, s) => _savePrefs(s.root, { email_digest: e.target.value })
      }
    },

    Mentions: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Mentions' },
        Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'How you receive mention notifications.' }
      },
      MentionSelect: {
        tag: 'select',
        padding: 'Y Z1',
        round: 'Y',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        cursor: 'pointer',
        childExtends: { tag: 'option', text: (el, s) => s.text || s.value || '' },
        childrenAs: 'state',
        children: (el, s) => {
          const cur = s.root?.userPrefs?.mention_notifications || 'realtime'
          return ['off', 'realtime', 'daily'].map(v => ({
            value: v,
            text: v === 'realtime' ? 'Realtime' : v.charAt(0).toUpperCase() + v.slice(1),
            selected: v === cur
          }))
        },
        onChange: (e, el, s) => _savePrefs(s.root, { mention_notifications: e.target.value })
      }
    },

    Birthdays: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Birthday reminders' },
      BirthdayToggle: {
        tag: 'button',
        padding: 'Y Z1',
        round: 'Y',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        background: (el, s) => s.root?.userPrefs?.birthday_reminders !== false ? 'green' : 'overlay',
        color: (el, s) => s.root?.userPrefs?.birthday_reminders !== false ? 'white' : 'caption',
        text: (el, s) => s.root?.userPrefs?.birthday_reminders !== false ? 'On' : 'Off',
        onClick: (e, el, s) => {
          const cur = s.root?.userPrefs?.birthday_reminders !== false
          _savePrefs(s.root, { birthday_reminders: !cur })
        }
      }
    }
  }
})

// ── Sessions (self only) ──
MemberProfile.ProfileSessions = _selfCard({
  attr: { 'data-content-section': 'Sessions' },
  gap: 'A',
  SessTitle: { tag: 'h2', fontSize: 'B', fontWeight: '700', color: 'title', text: 'Current session' },
  SessionInfo: {
    flow: 'y',
    gap: 'Z',
    padding: 'Z 0',
    Browser: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      RowLabel: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Browser' },
      RowValue: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        text: (el) => {
          if (typeof navigator === 'undefined') return '—'
          const ua = navigator.userAgent
          if (ua.includes('Chrome')) return 'Chrome'
          if (ua.includes('Firefox')) return 'Firefox'
          if (ua.includes('Safari')) return 'Safari'
          return 'Unknown'
        }
      }
    },
    Platform: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      RowLabel: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Platform' },
      RowValue: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        text: (el) => {
          if (typeof navigator === 'undefined') return '—'
          const p = navigator.platform || ''
          if (p.includes('Mac')) return 'macOS'
          if (p.includes('Win')) return 'Windows'
          if (p.includes('Linux')) return 'Linux'
          return p || 'Unknown'
        }
      }
    },
    SignedIn: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      RowLabel: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Status' },
      RowValue: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'green', text: 'Active now' }
    }
  }
})

// ── Danger zone (self only) ──
MemberProfile.ProfileDanger = {
  ..._card({}),
  attr: { 'data-content-section': 'Danger zone' },
  hide: (el) => !el.call('isSelf'),
  gap: 'B',
  borderColor: 'red',
  DangerTitle: { tag: 'h2', fontSize: 'B', fontWeight: '700', color: 'red', text: 'Danger zone' },
  DangerHint: { tag: 'p', fontSize: 'Z2', color: 'caption', text: 'These actions are irreversible. Please be sure before proceeding.' },

  SignOutRow: {
    flow: 'x',
    align: 'center space-between',
    padding: 'B 0',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Sign out' },
      Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'End your current session.' }
    },
    SignOutBtn: {
      tag: 'button',
      padding: 'Y A',
      round: 'Y',
      borderWidth: '1px',
      borderColor: (el, s) => s._confirming ? 'transparent' : 'red',
      borderStyle: 'solid',
      background: (el, s) => s._confirming ? 'red' : 'transparent',
      color: (el, s) => s._confirming ? 'white' : 'red',
      fontSize: 'Z2',
      fontWeight: '600',
      cursor: 'pointer',
      text: (el, s) => s._confirming ? 'Confirm sign out?' : 'Sign out',
      onClick: async (e, el, s) => {
        if (!s._confirming) {
          el.state.update({ _confirming: true })
          setTimeout(() => el.state.update({ _confirming: false }), 3000)
          return
        }
        await el.sdk.logout()
        let root = el; while (root && !root.routes) root = root.parent
        const router = el.context?.utils?.router
        if (router && root) router('/login', root, {}, { pushState: true })
      }
    }
  },

  DeactivateRow: {
    flow: 'x',
    align: 'center space-between',
    padding: 'B 0',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      LabelText: { tag: 'span', fontSize: 'A', fontWeight: '600', color: 'title', text: 'Deactivate account' },
      Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'Hides your profile and revokes access. Contact admin to reactivate.' }
    },
    DeactivateBtn: {
      tag: 'button',
      padding: 'Y A',
      round: 'Y',
      borderWidth: '1px',
      borderColor: (el, s) => s._confirming ? 'transparent' : 'red',
      borderStyle: 'solid',
      background: (el, s) => s._confirming ? 'red' : 'transparent',
      color: (el, s) => s._confirming ? 'white' : 'red',
      fontSize: 'Z2',
      fontWeight: '600',
      cursor: 'pointer',
      text: (el, s) => s._confirming ? 'Confirm deactivation?' : 'Deactivate',
      onClick: async (e, el, s) => {
        if (!s._confirming) {
          el.state.update({ _confirming: true })
          setTimeout(() => el.state.update({ _confirming: false }), 3000)
          return
        }
        try {
          await el.sdk.execute('workspaceProject.userProfiles', 'update', {
            id: s.root.currentUserId,
            payload: { status: 'left' }
          })
          await el.sdk.logout()
          let root = el; while (root && !root.routes) root = root.parent
          const router = el.context?.utils?.router
          if (router && root) router('/login', root, {}, { pushState: true })
        } catch (err) { console.warn('deactivate failed', err) }
      }
    }
  }
}

export const MemberProfileCheckItem = {
  flow: 'x',
  align: 'center',
  gap: 'Z1',
  padding: 'Z1 0',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  CheckIcon: {
    tag: 'span',
    fontSize: 'A',
    width: 'B',
    textAlign: 'center',
    color: (el, s) => (s.done ? 'green' : 'caption'),
    text: (el, s) => (s.done ? '✓' : '○')
  },
  CheckLabel: {
    tag: 'span',
    fontSize: 'A',
    flex: '1',
    color: (el, s) => (s.done ? 'title' : 'caption'),
    text: (el, s) => s.label
  },
  CheckStatus: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: (el, s) => (s.done ? 'green' : 'orange'),
    text: (el, s) => (s.done ? 'Done' : 'Pending')
  }
}

// Shared input styling for every grant-card field — keeps the row
// chrome consistent and keeps each field definition compact.
const _grantInputBase = {
  padding: 'Z Z1',
  round: 'Y',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  background: 'overlay',
  color: 'title',
  fontSize: 'Z2',
  width: '100%',
  boxSizing: 'border-box',
  ':focus': { borderColor: 'blue', outline: 'none' }
}

// ── One grant card — renders the 4 editable factors + derived totals ─
export const ProfileGrantCard = {
  flow: 'y',
  gap: 'A',
  padding: 'A',
  round: 'A',
  background: 'overlayStrong',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',

  GrantHead: {
    flow: 'x',
    align: 'center',
    gap: 'Z',

    // Grant name sits flush-left, auto-sized to its content so the share
    // class dropdown lands right beside it rather than being stretched
    // across the row by a flex:1 on the input.
    LabelInput: {
      tag: 'input',
      type: 'text',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      background: 'transparent',
      border: 'none',
      padding: '0',
      minWidth: '0',
      width: 'D2',
      placeholder: 'Grant name',
      onCreate: (el, s) => { el.node.value = s.label || '' },
      // Focus-guard sync: when root state updates elsewhere (e.g. after
      // a save), refresh the DOM value but never overwrite while the
      // user is actively typing here.
      onUpdate: (el, s) => {
        if (!el.node || el.node === document.activeElement) return
        const v = s.label || ''
        if (el.node.value !== v) el.node.value = v
      },
      onInput: (e, el, s) => {
        _patchGrant(s.root, s.id, { label: e.target.value })
        _keepFocus(e.target)
      }
    },
    // Stock class selector — A / B / C / Preferred / Common. Applies
    // to any grant (including investments, which carry a real class).
    // Direct sibling of LabelInput so the layout reads as
    // "Grant 1 [B ▾]" with the dropdown right next to the title.
    ShareClassSelect: {
      tag: 'select',
      padding: 'V Z',
      round: 'Y',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      background: 'overlay',
      color: 'title',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      flexShrink: '0',
      ':focus': { borderColor: 'blue', outline: 'none' },
      childExtends: {
        tag: 'option',
        attr: { value: (el, s) => s.value },
        text: (el, s) => s.text || s.value || ''
      },
      childrenAs: 'state',
      children: (el, s) => SHARE_CLASS_OPTIONS,
      onCreate: (el, s) => { if (el.node) el.node.value = s.share_class || '' },
      onUpdate: (el, s) => {
        if (!el.node || el.node === document.activeElement) return
        const v = s.share_class || ''
        if (String(el.node.value) !== String(v)) el.node.value = v
      },
      onChange: (e, el, s) => _patchGrant(s.root, s.id, { share_class: e.target.value })
    },

    SaveStatus: {
      flexShrink: '0',
      // Hidden inside the cap-table modal (CapTableGrantModal renders its
      // own save chip + buttons in the ModalFooter so the card stays
      // focused on the editable fields).
      hide: (el, s) =>
        (s.root?.grantDetail?.id === s.id) ||
        !(s.root?._grantSaveStatus || {})[s.id],
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      padding: 'V Z',
      round: 'V',
      background: (el, s) => {
        const st = (s.root?._grantSaveStatus || {})[s.id]
        if (st === 'saved') return 'green.12'
        if (st === 'error') return 'red.12'
        if (st === 'dirty') return 'yellow.12'
        return 'blue.12'
      },
      color: (el, s) => {
        const st = (s.root?._grantSaveStatus || {})[s.id]
        if (st === 'saved') return 'green'
        if (st === 'error') return 'red'
        if (st === 'dirty') return 'yellow'
        return 'blue'
      },
      text: (el, s) => {
        const st = (s.root?._grantSaveStatus || {})[s.id]
        if (st === 'saved') return '✓ Saved'
        if (st === 'error') return 'Save failed'
        if (st === 'dirty') return '● Unsaved'
        return 'Saving…'
      }
    },
    SaveBtn: {
      // Inside the cap-table modal, Save lives in the ModalFooter — so
      // hide this inline button there to keep the card uncluttered.
      hide: (el, s) => s.root?.grantDetail?.id === s.id,
      // `margin-left: auto` pushes the Save + Remove cluster to the
      // right edge regardless of whether SaveStatus is visible.
      marginLeft: 'auto',
      flexShrink: '0',
      tag: 'button',
      type: 'button',
      padding: 'V Z1',
      round: 'Z',
      border: 'none',
      background: (el, s) => ((s.root?._grantSaveStatus || {})[s.id] === 'dirty') ? 'blue' : 'overlayStrong',
      color: (el, s) => ((s.root?._grantSaveStatus || {})[s.id] === 'dirty') ? 'white' : 'subtitle',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      text: 'Save',
      ':hover': { opacity: '.9' },
      onClick: (e, el, s) => _flushGrant(s.root, s.id)
    },
    DeleteBtn: {
      // Same rationale as SaveBtn — the modal renders a trash icon in
      // its ModalHeader, so the inline Remove is hidden there.
      hide: (el, s) => s.root?.grantDetail?.id === s.id,
      tag: 'button',
      type: 'button',
      padding: 'V Z',
      round: 'Z',
      border: 'none',
      background: 'transparent',
      color: 'caption',
      fontSize: 'Z2',
      cursor: 'pointer',
      text: '× Remove',
      ':hover': { color: 'red' },
      onClick: async (e, el, s) => {
        if (!window.confirm(`Remove ${s.label || 'this grant'}?`)) return
        await _deleteGrant(s.root, s.id)
      }
    }
  },

  // Row 1 — for grants: dates + valuation; for investments: investment
  // amount + valuation (no dates — investments are paid up-front).
  // Grid collapses from 3 cols to 2 cols when the record is an
  // investment so the fields fill the width cleanly.
  FactorsGrid: {
    display: 'grid',
    gridTemplateColumns: (el, s) =>
      s.investment != null ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
    gap: 'A',

    StartField: {
      hide: (el, s) => s.investment != null,
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Start date' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'date',
        onCreate: (el, s) => { el.node.value = s.vesting_start || '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.vesting_start || ''
          if (el.node.value !== v) el.node.value = v
        },
        onInput: (e, el, s) => {
          _patchGrant(s.root, s.id, { vesting_start: e.target.value || null })
          _keepFocus(e.target)
        }
      }
    },
    EndField: {
      hide: (el, s) => s.investment != null,
      flow: 'y',
      gap: 'V',

      Label: {
        flow: 'x',
        align: 'center space-between',
        gap: 'Z',
        Text: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'End date' },
        // `_incomplete` is a pure UI-only toggle on the grant state.
        // When ticked, the card reveals an "Actual end date" input
        // that overwrites `vesting_end` with the day the contract
        // was cancelled/terminated early. Not persisted to the DB —
        // the user's intent flows through to the DB via the
        // overwritten `vesting_end`.
        IncompleteToggle: {
          flow: 'x',
          align: 'center',
          gap: 'V',
          cursor: 'pointer',
          Check: {
            tag: 'input',
            type: 'checkbox',
            margin: '0',
            cursor: 'pointer',
            onCreate: (el, s) => {
              el.node.checked = false
              el._lastGrantId = s.id
            },
            onUpdate: (el, s) => {
              if (!el.node || el._lastGrantId === s.id) return
              el._lastGrantId = s.id
              el.node.checked = false
              const wrap = el.node.closest('[data-key="EndField"]')?.querySelector('[data-key="ActualEndWrap"]')
              wrap?.style.setProperty('display', 'none', 'important')
            },
            onChange: (e, el) => {
              const wrap = el.node.closest('[data-key="EndField"]')?.querySelector('[data-key="ActualEndWrap"]')
              wrap?.style.setProperty('display', e.target.checked ? 'flex' : 'none', 'important')
            }
          },
          CheckLabel: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            fontWeight: '600',
            text: 'Incomplete'
          }
        }
      },

      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'date',
        onCreate: (el, s) => { el.node.value = s.vesting_end || '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.vesting_end || ''
          if (el.node.value !== v) el.node.value = v
        },
        onInput: (e, el, s) => {
          _patchGrant(s.root, s.id, { vesting_end: e.target.value || null })
          _keepFocus(e.target)
        }
      },

      // Actual end date — only visible when Incomplete is ticked.
      // Writing to this field overwrites `vesting_end` directly so the
      // DB-generated `shares` column (and the vested-shares trigger)
      // recompute from the true contract duration.
      ActualEndWrap: {
        style: { display: 'none' },
        flow: 'y',
        gap: 'V',
        padding: 'V 0 0 0',
        SubLabel: {
          tag: 'span',
          fontSize: 'Y1',
          color: 'orange',
          fontWeight: '600',
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          text: 'Actual end (overrides)'
        },
        ActualInput: {
          ..._grantInputBase,
          tag: 'input',
          type: 'date',
          onCreate: (el, s) => { el.node.value = s.vesting_end || '' },
          onUpdate: (el, s) => {
            if (!el.node || el.node === document.activeElement) return
            const v = s.vesting_end || ''
            if (el.node.value !== v) el.node.value = v
          },
          onInput: (e, el, s) => {
            _patchGrant(s.root, s.id, { vesting_end: e.target.value || null })
            _keepFocus(e.target)
          }
        }
      }
    },
    // Investment amount — only shown for investment records. Edits
    // cascade to shares_manual using the origin_valuation at the time
    // of purchase (investment / valuation × 10M pool).
    InvestmentAmountField: {
      hide: (el, s) => s.investment == null,
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Investment amount ($)' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        step: '100',
        placeholder: '0',
        onCreate: (el, s) => { el.node.value = s.investment ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.investment ?? ''
          if (String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          const raw = e.target.value
          const amount = raw === '' ? null : Number(raw)
          const patch = { investment: amount }
          // Recalculate the share count from the origin valuation.
          // Writes to shares_manual so the generated `shares` column
          // matches once it hits the DB.
          if (amount != null && amount > 0 && s.origin_valuation && s.origin_valuation > 0) {
            patch.shares_manual = Math.round((amount / s.origin_valuation) * 10000000)
          }
          _patchGrant(s.root, s.id, patch)
          _keepFocus(e.target)
        }
      }
    },
    ValuationField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Origin valuation' },
      Select: {
        ..._grantInputBase,
        tag: 'select',
        cursor: 'pointer',
        childExtends: {
          tag: 'option',
          text: (el, s) => s.text || s.value || ''
        },
        childrenAs: 'state',
        children: (el, s) => _valuationOptions(s.root),
        onCreate: (el, s) => {
          if (!el.node) return
          // Set option values imperatively since DOMQL's attr doesn't
          // support function-valued entries on childExtends options.
          const opts = _valuationOptions(s.root)
          Array.from(el.node.options).forEach((opt, i) => {
            if (opts[i]) opt.value = opts[i].value
          })
          el.node.value = String(s.origin_valuation || '')
        },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = String(s.origin_valuation || '')
          if (el.node.value !== v) el.node.value = v
        },
        onChange: (e, el, s) => {
          const ov = e.target.value === '' ? null : Number(e.target.value)
          const patch = { origin_valuation: ov }
          if (s.investment != null && s.investment > 0 && ov && ov > 0) {
            patch.shares_manual = Math.round((s.investment / ov) * 10000000)
          }
          _patchGrant(s.root, s.id, patch)
        }
      }
    }
  },

  // Investment metadata — type + discount. Informational only, does NOT
  // change share/equity calculations.
  InvestmentMetaRow: {
    hide: (el, s) => s.investment == null,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 'A',

    InvestmentTypeField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Investment type' },
      Select: {
        ..._grantInputBase,
        tag: 'select',
        cursor: 'pointer',
        childExtends: {
          tag: 'option',
          attr: { value: (el, s) => s.value },
          text: (el, s) => s.text || s.value || ''
        },
        childrenAs: 'state',
        children: (el, s) => [
          { value: '', text: '— select —' },
          { value: 'post-money-safe', text: 'Post-money SAFE' },
          { value: 'pre-money-safe', text: 'Pre-money SAFE' },
          { value: 'convertible-note', text: 'Convertible Note' },
          { value: 'priced-round', text: 'Priced Round' },
          { value: 'kiss', text: 'KISS' }
        ],
        onCreate: (el, s) => { if (el.node) el.node.value = s.investment_type || '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.investment_type || ''
          if (el.node.value !== v) el.node.value = v
        },
        onChange: (e, el, s) => _patchGrant(s.root, s.id, { investment_type: e.target.value || null })
      }
    },

    DiscountField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Discount (%)' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        max: '100',
        step: '1',
        placeholder: '0',
        onCreate: (el, s) => { el.node.value = s.discount ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.discount ?? ''
          if (String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          _patchGrant(s.root, s.id, { discount: e.target.value === '' ? null : Number(e.target.value) })
          _keepFocus(e.target)
        }
      }
    }
  },

  // Row 2 — compensation: monthly ↔ hourly auto-compute via hours/week.
  // Hidden for investments since money was paid as a lump sum, not via
  // a salary stream.
  CompRow: {
    hide: (el, s) => s.investment != null || s.share_class === SHARE_CLASS_ADVISOR,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 'A',

    // Monthly ↔ hourly are codependent via weekly_hours. When one side is
    // missing but the others exist, derive it for display so the field
    // never shows 0/empty while the partner value is present.
    SalaryField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: '$ per month' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        step: '100',
        placeholder: '0',
        value: (el, s) => s.salary_monthly ?? _deriveMonthly(s) ?? '',
        onCreate: (el, s) => { el.node.value = s.salary_monthly ?? _deriveMonthly(s) ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.salary_monthly ?? _deriveMonthly(s) ?? ''
          if (String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          const monthly = e.target.value === '' ? null : Number(e.target.value)
          const patch = { salary_monthly: monthly }
          const wh = Number(String(s.weekly_hours || '').replace(/[^0-9.]/g, '')) || 0
          // If hours/week is set, back-calc the hourly rate so the two stay
          // in sync. 4.33 weeks/month is the standard approximation.
          if (monthly != null && wh > 0) {
            patch.hourly_rate = Math.round((monthly / (wh * 4.33)) * 100) / 100
          }
          _patchGrant(s.root, s.id, patch)
          _keepFocus(e.target)
        }
      }
    },
    HourlyField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Hourly rate ($)' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        step: '1',
        placeholder: '0',
        value: (el, s) => s.hourly_rate ?? _deriveHourly(s) ?? '',
        onCreate: (el, s) => { el.node.value = s.hourly_rate ?? _deriveHourly(s) ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.hourly_rate ?? _deriveHourly(s) ?? ''
          if (String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          const hourly = e.target.value === '' ? null : Number(e.target.value)
          const patch = { hourly_rate: hourly }
          const wh = Number(String(s.weekly_hours || '').replace(/[^0-9.]/g, '')) || 0
          // Hourly × hours/week × 4.33 → derived monthly
          if (hourly != null && wh > 0) {
            patch.salary_monthly = Math.round(hourly * wh * 4.33)
          }
          _patchGrant(s.root, s.id, patch)
          _keepFocus(e.target)
        }
      }
    },
    WeeklyField: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Hours / week' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'text',
        placeholder: '40',
        onCreate: (el, s) => { el.node.value = s.weekly_hours ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.weekly_hours ?? ''
          if (el.node.value !== String(v)) el.node.value = v
        },
        // When hours/week changes, recompute whichever of monthly/hourly
        // is derivable so both fields reflect the new relationship.
        onInput: (e, el, s) => {
          const raw = e.target.value
          const wh = Number(String(raw || '').replace(/[^0-9.]/g, '')) || 0
          const patch = { weekly_hours: raw || null }
          if (wh > 0) {
            if (s.salary_monthly && !s.hourly_rate) {
              patch.hourly_rate = Math.round((s.salary_monthly / (wh * 4.33)) * 100) / 100
            } else if (s.hourly_rate && !s.salary_monthly) {
              patch.salary_monthly = Math.round(s.hourly_rate * wh * 4.33)
            }
          }
          _patchGrant(s.root, s.id, patch)
          _keepFocus(e.target)
        }
      }
    }
  },

  // ── Computed totals — editable contract total rewires salary_monthly ─
  // For investments: ContractTotal drops out (no salary stream) and the
  // grid collapses to 2 cols so Shares + Equity fill the width.
  TotalsRow: {
    display: 'grid',
    gridTemplateColumns: (el, s) =>
      (s.investment != null || s.share_class === SHARE_CLASS_ADVISOR) ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
    gap: 'A',
    padding: 'A 0 0 0',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line',

    ContractTotal: {
      hide: (el, s) => s.investment != null || s.share_class === SHARE_CLASS_ADVISOR,
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Contract total' },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        step: '100',
        fontSize: 'A',
        fontWeight: '700',
        onCreate: (el, s) => { el.node.value = _contractValue(s) || '' },
        onUpdate: (el, s) => {
          const v = _contractValue(s) || ''
          if (el.node && el.node !== document.activeElement && String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          const total = Number(e.target.value) || 0
          if (!s.vesting_start || !s.vesting_end) return
          const months = (new Date(s.vesting_end) - new Date(s.vesting_start)) / (1000 * 60 * 60 * 24 * 30.4375)
          if (months <= 0) return
          _patchGrant(s.root, s.id, { salary_monthly: Math.round(total / months) })
          _keepFocus(e.target)
        }
      }
    },
    // Shares cell — editable. Writes to `shares_manual`, which overrides
    // the generated `shares` column. Editing this back-calculates the
    // other derived money fields (salary, hourly, contract total) so the
    // record stays internally consistent. Hours/week stays untouched —
    // it's a user-defined factor, not a derived one.
    SharesCell: {
      flow: 'y',
      gap: 'V',
      Label: {
        flow: 'x',
        align: 'center',
        gap: 'V',
        Text: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Shares' },
        // Little visual cue when the value is a manual override (so admins
        // can tell at a glance it's not the derived number).
        OverrideTag: {
          hide: (el, s) => s.shares_manual == null,
          tag: 'span',
          fontSize: 'Y1',
          padding: 'V V',
          round: 'V',
          fontWeight: '700',
          color: 'orange',
          background: 'orange.12',
          text: 'MANUAL'
        }
      },
      Input: {
        ..._grantInputBase,
        tag: 'input',
        type: 'number',
        min: '0',
        step: '1',
        fontSize: 'A',
        fontWeight: '700',
        placeholder: (el, s) => String(_liveShares(s) || 0),
        // Reactive `value` binding — subscribes the element to
        // `shares_manual` so DOMQL re-runs onUpdate whenever another
        // field (Investment Amount, Origin valuation, etc.) cascades
        // a new share count into it.
        value: (el, s) => s.shares_manual ?? '',
        // Show the override value when set; otherwise leave empty so the
        // placeholder (= derived shares) hints at the value that will
        // apply if left blank.
        onCreate: (el, s) => { el.node.value = s.shares_manual ?? '' },
        onUpdate: (el, s) => {
          if (!el.node || el.node === document.activeElement) return
          const v = s.shares_manual ?? ''
          if (String(el.node.value) !== String(v)) el.node.value = v
        },
        onInput: (e, el, s) => {
          const raw = e.target.value
          const sharesManual = raw === '' ? null : Number(raw)
          const patch = { shares_manual: sharesManual }
          // Cascade shares → salary → hourly. Keep hours/week fixed per
          // user requirement. Formula: salary = shares × valuation ÷
          // (months × 10M_pool).
          if (
            sharesManual != null && sharesManual > 0 &&
            s.origin_valuation && s.origin_valuation > 0 &&
            s.vesting_start && s.vesting_end
          ) {
            const months = (new Date(s.vesting_end) - new Date(s.vesting_start)) / (1000 * 60 * 60 * 24 * 30.4375)
            if (months > 0) {
              const newSalary = Math.round((sharesManual * s.origin_valuation) / (10000000 * months))
              patch.salary_monthly = newSalary
              const wh = Number(String(s.weekly_hours || '').replace(/[^0-9.]/g, '')) || 0
              if (wh > 0) {
                patch.hourly_rate = Math.round((newSalary / (wh * 4.33)) * 100) / 100
              }
            }
          }
          _patchGrant(s.root, s.id, patch)
          _keepFocus(e.target)
        }
      }
    },
    EquityCell: {
      flow: 'y',
      gap: 'V',
      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.04em', text: 'Equity' },
      Value: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'green',
        // Compute live from current factors so the display stays in
        // sync as the user edits shares / salary / valuation. The DB
        // generated column (`s.equity_pct`) only refreshes on save.
        text: (el, s) => {
          const pct = _liveEquityPct(s)
          return pct > 0 ? `${pct.toFixed(2)}%` : '—'
        }
      }
    }
  },

  // ── Vested — its own row below the totals grid so the main grid can
  // stay 3-col wide and the vesting readout gets visual breathing room.
  // Hidden inside the cap-table modal (modal renders its own vested row
  // in the TotalsBlock) and for investments (no vesting schedule).
  VestedRow: {
    hide: (el, s) =>
      s.root?.grantDetail?.id === s.id || s.investment != null,
    flow: 'x',
    align: 'center space-between',
    padding: 'A 0 0 0',
    gap: 'A',

    VestedCell: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      Label: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        text: 'Vested'
      },
      Value: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const pct = s.shares ? Math.round(((s.vested_shares || 0) / s.shares) * 100) : 0
          return `${_numberFmt.format(s.vested_shares || 0)} · ${pct}%`
        }
      }
    },

    // Visual progress bar for vested portion — gives the section weight
    // once it's on its own row rather than squeezed into a 4-col grid.
    VestedBar: {
      flex: '1',
      maxWidth: 'E',
      height: 'Y',
      round: 'Y',
      background: 'line',
      overflow: 'hidden',
      Fill: {
        height: '100%',
        round: 'Y',
        background: 'green',
        width: (el, s) => {
          if (!s.shares) return '0%'
          const pct = Math.min(100, Math.round(((s.vested_shares || 0) / s.shares) * 100))
          return `${pct}%`
        }
      }
    }
  }
}
